import { get } from 'svelte/store'
import type { Object3D } from 'three'
import { canvasMultiSelection, canvasSelection } from '../stores/canvas-selection'
import { ThreeItemKeys, ThreeItemTypes } from '../constants/three'
import { applySelectionMaterial, restoreOriginalMaterial } from '../helpers/selection-material'
import { sceneMode } from '../stores/scene-mode'

const useCanvasSelection = () => {
  const isEmpty = () => {
    return !get(canvasSelection)
  }

  const hasSingleItem = () => {
    return !!get(canvasSelection) && !get(canvasMultiSelection).length
  }

  const hasMultipleItems = () => {
    return !!get(canvasMultiSelection).length
  }

  const hasSinglePrinter = () => {
    return get(canvasSelection)?.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.PRINTER
  }

  const hasSingleFile = () => {
    return get(canvasSelection)?.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.FILE
  }

  const hasSingleGroup = () => {
    return get(canvasSelection)?.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.GROUP
  }

  const hasSingleForbiddenZone = () => {
    return get(canvasSelection)?.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.FORBIDDEN_ZONE
  }

  const allSelected = () => {
    const primary = get(canvasSelection)
    const rest = get(canvasMultiSelection)
    return primary ? [primary, ...rest] : rest
  }

  // A selected group stands in for every file it contains: batch actions
  // (e.g. assigning a printer) should apply to all of them.
  const getSelectedFileIds = () => {
    const fileIds = new Set<string>()

    allSelected().forEach((item) => {
      if (item.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.FILE) {
        fileIds.add(item.uuid)
      }
      else if (item.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.GROUP) {
        item.traverse((child) => {
          if (child.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.FILE) {
            fileIds.add(child.uuid)
          }
        })
      }
    })

    return [...fileIds]
  }

  // A solo group stands in for the single file it contains: batch actions
  // (e.g. assigning a printer) apply to it as if it were that file.
  const hasSingleSoloGroup = () => {
    return hasSingleGroup() && getSelectedFileIds().length === 1
  }

  // A file alone in its group is presented as if there were no group at all:
  // it must never end up as the actual selection, no matter which entry
  // point (raycast, a left-bar row, a second click on an already-selected
  // object, ...) produced it - so this is enforced here, once, rather than
  // at each call site.
  const resolveSoloGroupTarget = (value: Object3D) => {
    const parent = value.parent

    if (
      value.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.FILE
      && parent?.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.GROUP
      // Selecting the group itself attaches a transient Box3Helper as one of
      // its children (see applySelectionMaterial) - it must not count
      // towards "how many files does this group have".
      && parent.children.filter(child => !child.userData[ThreeItemKeys.TRANSIENT]).length === 1
    ) {
      return parent
    }

    return value
  }

  const set = (value: Object3D) => {
    value = resolveSoloGroupTarget(value)
    const previousSelection = get(canvasSelection)

    if (previousSelection) {
      restoreOriginalMaterial(previousSelection)
    }

    if (value) {
      applySelectionMaterial(value)
    }

    canvasSelection.set(value)

    const previousMulti = get(canvasMultiSelection)
    previousMulti.forEach(restoreOriginalMaterial)
    canvasMultiSelection.set([])
  }

  const add = (value: Object3D) => {
    value = resolveSoloGroupTarget(value)
    const primary = get(canvasSelection)

    if (!primary) {
      set(value)
      return
    }

    if (primary.uuid === value.uuid) return

    const current = get(canvasMultiSelection)
    const existing = current.find(object => object.uuid === value.uuid)

    if (existing) {
      restoreOriginalMaterial(existing)
      canvasMultiSelection.set(current.filter(object => object.uuid !== value.uuid))
    }
    else {
      applySelectionMaterial(value)
      canvasMultiSelection.set([...current, value])
    }
  }

  const clear = () => {
    const currentSelection = get(canvasSelection)
    if (currentSelection) restoreOriginalMaterial(currentSelection)
    canvasSelection.set(undefined)

    const currentMulti = get(canvasMultiSelection)
    currentMulti.forEach(restoreOriginalMaterial)
    canvasMultiSelection.set([])
  }

  const upsert = (value: Object3D, shouldAppend?: boolean) => {
    if (shouldAppend) add(value)
    else set(value)
    sceneMode.set('translate')
  }

  const has = (id: string) => {
    return get(canvasSelection)?.uuid === id || get(canvasMultiSelection).some(object => object.uuid === id)
  }

  return {
    object: canvasSelection,
    multiSelection: canvasMultiSelection,
    upsert,
    isEmpty,
    has,
    clear,
    hasSinglePrinter,
    hasSingleItem,
    hasSingleFile,
    hasSingleGroup,
    hasSingleSoloGroup,
    hasSingleForbiddenZone,
    hasMultipleItems,
    getSelectedFileIds,
  }
}

export default useCanvasSelection
